import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { getDeckStudioStateForDeal, getDeckStudioReport, getIrrEstimatedDurationSeconds } from "@/lib/deck-studio.functions";
import { track } from "@/lib/track";
import { useElapsedSeconds, formatMSS } from "@/lib/use-elapsed";
import { DealPageHeader } from "@/components/deal-page-header";
import { DealNav } from "@/components/deal-nav";

function friendlyFailureMessage(reason: string | null | undefined): string {
  if (reason === "no_source_materials" || reason === "no_uploaded_materials") {
    return "This report reviews your pitch materials, but this deal has no uploaded deck or document to analyze. Add a pitch deck or document on the deal, then run the report again.";
  }
  if (reason && reason.includes("no slide images")) {
    return "We couldn't read any slides from the uploaded file. Make sure you've uploaded a readable PDF, PPTX, or DOCX, then run the report again.";
  }
  return "Something went wrong generating this report. Please try again. If it keeps failing, contact support.";
}

function ProcessingBlock({
  createdAt,
  estimateSeconds,
  estimateSettled,
}: {
  createdAt: string;
  estimateSeconds: number | null;
  estimateSettled: boolean;
}) {
  const elapsed = useElapsedSeconds(createdAt) ?? 0;
  // Fall back to 5 minutes if the estimate is null or the query failed.
  const estMinutes = estimateSettled
    ? Math.max(1, Math.round((estimateSeconds ?? 300) / 60))
    : null;
  return (
    <div className="space-y-2">
      <p className="text-[15px] text-brand-muted">
        Your report is generating now. This usually takes a minute or two. This page will update when it is ready, or refresh to check.
      </p>
      <p className="text-[14px] tabular-nums text-brand-muted">
        Elapsed {formatMSS(elapsed)}
        {estMinutes != null && <> / ~{estMinutes} min</>}
        {" "}
        <span className="text-brand-muted/80">(typical report: ~3–8 minutes)</span>
      </p>
    </div>
  );
}

export const Route = createFileRoute("/_authed/deals/$dealId_/deck-studio")({
  head: () => ({
    meta: [
      { title: "Investor Readiness Report: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  validateSearch: (s): { version?: number; versionPicker?: "open" } => ({
    version:
      typeof s.version === "number"
        ? s.version
        : typeof s.version === "string"
        ? Number(s.version) || undefined
        : undefined,
    versionPicker:
      (s as { versionPicker?: unknown }).versionPicker === "open"
        ? "open"
        : undefined,
  }),
  component: DeckStudioReportPage,
});

function DeckStudioReportPage() {
  const { dealId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const fetchState = useServerFn(getDeckStudioStateForDeal);
  const fetchReport = useServerFn(getDeckStudioReport);
  const fetchEstimate = useServerFn(getIrrEstimatedDurationSeconds);
  const queryClient = useQueryClient();

  const versionSelectRef = React.useRef<HTMLSelectElement | null>(null);
  const [pickerPulse, setPickerPulse] = React.useState(false);

  React.useEffect(() => {
    if (search.versionPicker !== "open") return;
    const el = versionSelectRef.current;
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus();
      setPickerPulse(true);
      window.setTimeout(() => setPickerPulse(false), 2000);
    }
    navigate({
      to: "/deals/$dealId/deck-studio",
      params: { dealId },
      search: search.version ? { version: search.version } : {},
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.versionPicker]);

  const state = useQuery({
    queryKey: ["deck-studio-state", dealId],
    queryFn: () => fetchState({ data: { dealId } }),
  });

  const report = useQuery({
    queryKey: ["deck-studio-report", dealId, search.version ?? null],
    queryFn: () =>
      fetchReport({
        data: { dealId, version: search.version },
      }),
    retry: false,
    refetchInterval: (q) => {
      const status = (q.state.data as { report_status?: string } | undefined)
        ?.report_status;
      return status === "processing" ? 4000 : false;
    },
  });

  const estimate = useQuery({
    queryKey: ["irr-estimated-duration"],
    queryFn: () => fetchEstimate({}),
    staleTime: 5 * 60 * 1000,
    enabled: report.data?.report_status === "processing",
  });

  const lastStatusRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const status = report.data?.report_status ?? null;
    if (
      lastStatusRef.current === "processing" &&
      (status === "complete" || status === "failed")
    ) {
      void queryClient.invalidateQueries({
        queryKey: ["deck-studio-state", dealId],
      });
    }
    lastStatusRef.current = status;
  }, [report.data?.report_status, dealId, queryClient]);

  const trackedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const d = report.data;
    if (!d || d.report_status !== "complete") return;
    if (trackedRef.current === d.id) return;
    trackedRef.current = d.id;
    track(
      "irr_viewed",
      { version: d.version_number, score: d.investor_readiness_score },
      { deal_id: dealId, report_id: d.id },
    );
  }, [report.data?.id, report.data?.report_status, report.data?.version_number, report.data?.investor_readiness_score, dealId]);

  if (state.isLoading || report.isLoading) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-10 text-sm text-brand-muted">
        Loading…
      </main>
    );
  }

  if (report.error || !report.data) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-10 space-y-4">
        <h1 className="font-serif text-2xl text-brand-ink">Report not found</h1>
        <p className="text-sm text-brand-muted">
          No Investor Readiness Report exists yet for this deal, or the
          requested version is missing.
        </p>
        <Button variant="outline" asChild>
          <Link to="/deals/$dealId" params={{ dealId }}>
            Back to deal
          </Link>
        </Button>
      </main>
    );
  }

  const r = report.data as {
    id: string;
    version_number: number;
    report_status: string;
    company_name: string | null;
    investor_readiness_score: number | null;
    recommendation: string | null;
    source_extraction_quality: string | null;
    source_slides_analyzed: number | null;
    source_slides_skipped: number | null;
    source_limitations: string[] | null;
    failed_reason: string | null;
    report_json: ReportJson | null;
    created_at: string;
    source_files: string[];
  };
  const versions = state.data?.versions ?? [];
  const rj = r.report_json;

  return (
    <main className="mx-auto max-w-3xl px-6 py-10 space-y-6">
      <div className="space-y-6 print:hidden">
        <DealPageHeader dealId={dealId} />
        <DealNav dealId={dealId} active="reports" />
      </div>
      <header className="space-y-3">
        <Link
          to="/deals/$dealId"
          params={{ dealId }}
          className="text-xs text-brand-muted no-underline hover:text-brand-ink"
        >
          ← Back to deal
        </Link>
        <div>
          <h1 className="font-serif text-3xl text-brand-ink">
            CapitalDart Investor Readiness Report
          </h1>
          <p className="mt-1 text-[14px] text-brand-muted">
            A practical review of what investors will like, what they will
            challenge, and what to fix before outreach.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-[14px] text-brand-muted">
          <span>Version {r.version_number}</span>
          <span aria-hidden>·</span>
          <span>{new Date(r.created_at).toLocaleDateString()}</span>
          {r.report_status === "processing" && (
            <span className="rounded-full bg-brand-processing px-2 py-0.5 text-[12px] font-medium text-white animate-[pulse-processing_1.5s_ease-in-out_infinite]">
              Processing
            </span>
          )}
          {versions.length > 1 && (
            <>
              <span aria-hidden>·</span>
              <select
                ref={versionSelectRef}
                className={`rounded-md border border-input bg-background px-2 py-1 text-xs transition-shadow ${
                  pickerPulse ? "ring-2 ring-brand-accent" : ""
                }`}
                value={r.version_number}
                onChange={(e) =>
                  navigate({
                    to: "/deals/$dealId/deck-studio",
                    params: { dealId },
                    search: { version: Number(e.target.value) },
                  })
                }
              >
                {versions.map((v) => (
                  <option key={v.id} value={v.version_number}>
                    Version {v.version_number}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      </header>

      {/* Top action row — mirrors the bottom row */}
      {r.report_status !== "processing" && (
      <section className="flex flex-wrap gap-2 print:hidden">
        {r.report_status === "complete" && (
          <Button
            onClick={() => {
              const slug = (r.company_name ?? "deal")
                .toLowerCase()
                .replace(/[\\/:*?"<>|]+/g, "")
                .replace(/['`]+/g, "")
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-+|-+$/g, "")
                .slice(0, 80) || "deal";
              const original = document.title;
              document.title = `${slug}-IRR`;
              const restore = () => {
                document.title = original;
                window.removeEventListener("afterprint", restore);
              };
              window.addEventListener("afterprint", restore);
              window.print();
            }}
            variant="default"
          >
            Download PDF
          </Button>
        )}
        <Button asChild variant="outline">
          <Link to="/app" search={{ dealId } as never}>
            Find Investors for This Deal
          </Link>
        </Button>
      </section>
      )}

      {r.source_files.length > 0 && (
        <section className="text-[14px] text-brand-muted">
          {r.source_files.length === 1 ? (
            <p>This analysis is based on: {r.source_files[0]}</p>
          ) : (
            <>
              <p>This analysis is based on the following documents you uploaded:</p>
              <ul className="mt-1 list-disc pl-5">
                {r.source_files.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      {/* Status / score block */}
      <section className="rounded-2xl border border-brand-line bg-brand-paper p-5">
        {r.report_status === "complete" && (
          <div className="flex items-baseline gap-3">
            <span className="text-[12px] font-medium uppercase tracking-[0.18em] text-brand-muted">
              Investor Readiness Score
            </span>
            <span className="font-serif text-[48px] tabular-nums text-brand-ink">
              {r.investor_readiness_score ?? "—"}
            </span>
            <span className="text-[14px] text-brand-muted">/ 100</span>
            {r.recommendation && (
              <span className="ml-auto rounded-full bg-brand-accent/15 px-3 py-1 text-[12px] font-medium text-brand-accent">
                {humanRecommendation(r.recommendation)}
              </span>
            )}
          </div>
        )}
        {r.report_status === "processing" && (
          <ProcessingBlock
            createdAt={r.created_at}
            estimateSeconds={estimate.data?.seconds ?? null}
            estimateSettled={estimate.isFetched || estimate.isError}
          />
        )}
        {r.report_status === "failed" && (
          <p className="text-[15px] text-destructive">
            {friendlyFailureMessage(r.failed_reason)}
          </p>
        )}
      </section>

      {/* Source quality */}
      {(r.source_extraction_quality ||
        r.source_slides_analyzed != null ||
        r.source_slides_skipped != null) && (
        <section className="rounded-2xl border border-brand-line bg-brand-paper p-5">
          <h2 className="text-[12px] font-medium uppercase tracking-[0.18em] text-brand-muted">
            Source quality
          </h2>
          <dl className="mt-3 grid grid-cols-3 gap-3 text-[14px]">
            <div>
              <dt className="text-brand-muted">Extraction Quality</dt>
              <dd className="text-brand-ink">
                {r.source_extraction_quality ?? "—"}
              </dd>
              <div className="mt-0.5 text-[12px] text-brand-muted">
                how cleanly we read your materials
              </div>
            </div>
            <div>
              <dt className="text-brand-muted">Slides Reviewed</dt>
              <dd className="text-brand-ink">
                {r.source_slides_analyzed ?? "—"}
              </dd>
              <div className="mt-0.5 text-[12px] text-brand-muted">
                slides analyzed by AI
              </div>
            </div>
            <div>
              <dt className="text-brand-muted">Slides Excluded</dt>
              <dd className="text-brand-ink">
                {r.source_slides_skipped ?? "—"}
              </dd>
              <div className="mt-0.5 text-[12px] text-brand-muted">
                dividers and boilerplate skipped
              </div>
            </div>
          </dl>
          {Array.isArray(r.source_limitations) &&
            r.source_limitations.length > 0 && (
              <ul className="mt-3 list-disc pl-5 text-[14px] text-brand-muted">
                {r.source_limitations.map((l, i) => (
                  <li key={i}>{l}</li>
                ))}
              </ul>
            )}
        </section>
      )}

      {/* Full report — 13 sections */}
      {r.report_status === "complete" && rj && <ReportBody rj={rj} />}

      <section className="flex flex-wrap gap-2 print:hidden">
        {r.report_status === "complete" && (
          <Button
            onClick={() => {
              const slug = (r.company_name ?? "deal")
                .toLowerCase()
                .replace(/[\\/:*?"<>|]+/g, "")
                .replace(/['`]+/g, "")
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-+|-+$/g, "")
                .slice(0, 80) || "deal";
              const original = document.title;
              document.title = `${slug}-IRR`;
              const restore = () => {
                document.title = original;
                window.removeEventListener("afterprint", restore);
              };
              window.addEventListener("afterprint", restore);
              window.print();
            }}
            variant="default"
          >
            Download PDF
          </Button>
        )}
        <Button asChild variant="outline">
          <Link to="/deals/$dealId" params={{ dealId }}>
            Back to deal
          </Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/app" search={{ dealId } as never}>
            Find Investors for This Deal
          </Link>
        </Button>
      </section>

      <footer className="border-t border-brand-line pt-4 text-[13px] text-brand-muted">
        This readiness score is based on the pitch materials and deal
        information provided. It does not include a review of the company's
        financial model, and figures or projections were not independently
        verified. This report is for informational and presentation-improvement purposes
        only. It is not legal, tax, accounting, investment, or securities
        advice. Consult qualified professional advisors before raising capital.
      </footer>
    </main>
  );
}

// ---------------------------------------------------------------------------
// 15-section renderer (back-compat with older 13-section reports)
// ---------------------------------------------------------------------------

type Severity = "low" | "medium" | "high";
type Impact = "low" | "medium" | "high";
type DataRoomStatus =
  | "confirmed"
  | "partial"
  | "to_confirm"
  | "have"
  | "missing";
type Readiness = "ready" | "needs_work";
type QuestionCategory =
  | "financials_and_unit_economics"
  | "team_and_execution"
  | "market_and_competition"
  | "risk_and_compliance"
  | "use_of_proceeds_and_structure";

interface EmailBlock {
  subject: string;
  body: string;
}

type OutreachLegacy = {
  cold_investor_email: EmailBlock;
  linkedin_message: string;
  followup_email: EmailBlock;
  warm_intro_request: string;
};
type OutreachV2 = {
  cold_emails: {
    institutional_lead: EmailBlock;
    strategic_lead: EmailBlock;
    momentum_driven: EmailBlock;
  };
  linkedin_message: string;
  follow_up_sequence: {
    seven_day: EmailBlock;
    fourteen_day: EmailBlock;
    ceo_direct: EmailBlock;
  };
  warm_intro_request: string;
};
type Outreach = OutreachV2 | OutreachLegacy;

interface ReportJson {
  executive_summary: string;
  investor_readiness_score: {
    overall: number;
    dimensions: Record<string, number>;
  };
  likely_investor_reaction: string;
  strengths?: Array<{
    strength: string;
    how_to_leverage: string;
    where_to_apply: string;
  }>;
  top_investor_objections: Array<{ objection: string; severity: Severity }>;
  investor_questions?: Array<{
    category: QuestionCategory;
    question: string;
    suggested_response: string;
    readiness: Readiness;
  }>;
  slide_by_slide_review: Array<{
    slide_index: number;
    summary: string;
    headline_strength: string;
    severity: Severity;
    recommended_changes: string[];
  }>;
  recommended_deck_changes: Array<{
    change: string;
    impact: Impact;
    effort: Impact;
  }>;
  missing_slides_or_cuts: { missing: string[]; cut_or_compress: string[] };
  revised_headlines_and_bullets: Array<{
    slide_index: number;
    headline: string;
    bullets: string[];
  }>;
  outreach_readiness_review: string;
  template_outreach_copy: Outreach;
  investor_fit_analysis: {
    fit: string;
    rationale: string;
    investor_types_to_target: string[];
    lead_profile?: {
      required_profile: string;
      why: string;
      proof_still_missing: string[];
    };
  };
  data_room_checklist: Array<{
    item: string;
    status: DataRoomStatus;
  }>;
  final_recommendation: {
    recommendation: string;
    rationale: string;
    projected_score_after_remediation?: {
      score_range_low: number;
      score_range_high: number;
      unlocks_audience: string;
    };
  };
  exec_summary_synthesis?: {
    narrative: string;
    top_priorities: Array<{
      title: string;
      source: "objection" | "change";
      level: "low" | "medium" | "high";
      why_it_matters: string;
    }>;
    investor_reaction_summary: string;
    next_step: string;
  } | null;
}

const QUESTION_CATEGORY_ORDER: QuestionCategory[] = [
  "financials_and_unit_economics",
  "team_and_execution",
  "market_and_competition",
  "risk_and_compliance",
  "use_of_proceeds_and_structure",
];

const QUESTION_CATEGORY_LABEL: Record<QuestionCategory, string> = {
  financials_and_unit_economics: "Financials & Unit Economics",
  team_and_execution: "Team & Execution",
  market_and_competition: "Market & Competition",
  risk_and_compliance: "Risk & Compliance",
  use_of_proceeds_and_structure: "Use of Proceeds & Structure",
};

function ReportBody({ rj }: { rj: ReportJson }) {
  const oc = rj.template_outreach_copy;
  const isV2 = (o: Outreach): o is OutreachV2 =>
    "cold_emails" in o && "follow_up_sequence" in o;
  return (
    <div className="space-y-6">
      <Section title="1. Executive Summary">
        {rj.exec_summary_synthesis ? (
          <ExecSummarySynthesisView
            synth={rj.exec_summary_synthesis}
            strengths={rj.strengths}
          />
        ) : (
          <p className="whitespace-pre-line text-[14px] leading-relaxed text-brand-ink">
            {rj.executive_summary}
          </p>
        )}
      </Section>

      <Section title="2. Investor Readiness Score">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-[14px] sm:grid-cols-3">
          {Object.entries(rj.investor_readiness_score.dimensions).map(
            ([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-2">
                <dt className="text-brand-muted">{humanize(k)}</dt>
                <dd className="tabular-nums font-medium text-brand-ink">{v}</dd>
              </div>
            ),
          )}
        </dl>
      </Section>

      {rj.strengths && rj.strengths.length > 0 && (
        <Section title="3. Key Strengths to Leverage">
          <ul className="space-y-3 text-[15px]">
            {rj.strengths.map((s, i) => (
              <li
                key={i}
                className="rounded-lg border border-brand-line bg-brand-paper/60 p-3"
              >
                <div className="font-medium text-brand-ink">{s.strength}</div>
                <p className="mt-1 text-brand-ink">{s.how_to_leverage}</p>
                <div className="mt-1 text-[12px] text-brand-muted">
                  {s.where_to_apply}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="4. Likely Investor Reaction">
        <p className="whitespace-pre-line text-[14px] leading-relaxed text-brand-ink">
          {rj.likely_investor_reaction}
        </p>
      </Section>

      <Section title="5. Top Investor Objections">
        <ul className="space-y-2 text-[15px]">
          {rj.top_investor_objections.map((o, i) => (
            <li key={i} className="flex items-start gap-2">
              <SeverityPill s={o.severity} />
              <span className="text-brand-ink">{o.objection}</span>
            </li>
          ))}
        </ul>
      </Section>

      {rj.investor_questions && rj.investor_questions.length > 0 && (
        <Section title="6. Likely Investor Questions">
          <div className="space-y-4 text-[15px]">
            {QUESTION_CATEGORY_ORDER.map((cat) => {
              const items = rj.investor_questions!.filter(
                (q) => q.category === cat,
              );
              if (items.length === 0) return null;
              return (
                <div key={cat}>
                  <h3 className="mb-2 text-[14px] font-medium uppercase tracking-[0.18em] text-brand-muted">
                    {QUESTION_CATEGORY_LABEL[cat]}
                  </h3>
                  <ul className="space-y-2">
                    {items.map((q, i) => (
                      <li
                        key={i}
                        className="rounded-lg border border-brand-line bg-brand-paper/60 p-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-brand-ink">{q.question}</span>
                          <ReadinessPill r={q.readiness} />
                        </div>
                        <div className="mt-1 pl-3 text-[14px] text-brand-muted">
                          {q.suggested_response}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      <Section title="7. Slide-by-Slide Review">
        <p className="mb-3 text-[12px] leading-snug text-brand-muted">
          Severity reflects priority of remediation. HIGH = institutional
          blocker, MEDIUM = meaningful gap, LOW = polish item. Headline status
          indicates whether the slide's main point lands clearly.
        </p>
        <ol className="space-y-3 text-[15px]">
          {rj.slide_by_slide_review.map((s) => (
            <li
              key={s.slide_index}
              className="rounded-lg border border-brand-line bg-brand-paper/60 p-3"
            >
              <div className="mb-1 flex items-center gap-2">
                <span className="text-[12px] font-medium uppercase tracking-[0.15em] text-brand-muted">
                  Slide {s.slide_index}
                </span>
                <SeverityPill s={s.severity} />
                <span className="text-[12px] text-brand-muted">
                  headline: {s.headline_strength}
                </span>
              </div>
              <p className="text-brand-ink">{s.summary}</p>
              {s.recommended_changes.length > 0 && (
                <ul className="mt-2 list-disc pl-5 text-brand-muted">
                  {s.recommended_changes.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      </Section>

      <Section title="8. Recommended Deck Changes">
        <ul className="space-y-2 text-[15px]">
          {rj.recommended_deck_changes.map((c, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="rounded bg-brand-line px-2 py-0.5 text-[12px] text-brand-muted">
                {c.impact} impact · {c.effort} effort
              </span>
              <span className="text-brand-ink">{c.change}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="9. Missing Slides / Cuts">
        <div className="grid gap-4 text-[15px] sm:grid-cols-2">
          <div>
            <h3 className="mb-1 text-[12px] font-medium uppercase tracking-[0.15em] text-brand-muted">
              Missing
            </h3>
            <ul className="list-disc pl-5 text-brand-ink">
              {rj.missing_slides_or_cuts.missing.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="mb-1 text-[12px] font-medium uppercase tracking-[0.15em] text-brand-muted">
              Cut or compress
            </h3>
            <ul className="list-disc pl-5 text-brand-ink">
              {rj.missing_slides_or_cuts.cut_or_compress.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section title="10. Revised Headlines & Bullets">
        <ol className="space-y-3 text-[15px]">
          {rj.revised_headlines_and_bullets.map((r) => (
            <li
              key={r.slide_index}
              className="rounded-lg border border-brand-line bg-brand-paper/60 p-3"
            >
              <div className="text-[12px] uppercase tracking-[0.15em] text-brand-muted">
                Slide {r.slide_index}
              </div>
              <div className="mt-1 font-medium text-brand-ink">{r.headline}</div>
              {r.bullets.length > 0 && (
                <ul className="mt-1 list-disc pl-5 text-brand-ink">
                  {r.bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      </Section>

      <Section title="11. Outreach Readiness Review">
        <p className="whitespace-pre-line text-[14px] leading-relaxed text-brand-ink">
          {rj.outreach_readiness_review}
        </p>
      </Section>

      <Section title="12. Investor Fit Analysis">
        <p className="text-[15px] text-brand-ink">
          <span className="font-medium">Fit:</span> {rj.investor_fit_analysis.fit}
        </p>
        <p className="mt-2 whitespace-pre-line text-[14px] leading-relaxed text-brand-ink">
          {rj.investor_fit_analysis.rationale}
        </p>
        {rj.investor_fit_analysis.investor_types_to_target.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {rj.investor_fit_analysis.investor_types_to_target.map((t, i) => (
              <span
                key={i}
                className="rounded-full bg-brand-line px-2 py-0.5 text-[13px] text-brand-ink"
              >
                {t}
              </span>
            ))}
          </div>
        )}
        {rj.investor_fit_analysis.lead_profile && (
          <div className="mt-4 border-t border-brand-line pt-4">
            <h3 className="mb-2 text-[12px] font-medium uppercase tracking-[0.18em] text-brand-muted">
              Lead Investor Profile
            </h3>
            <div className="space-y-2 text-[15px] text-brand-ink">
              <p className="whitespace-pre-line">
                <span className="font-medium">Required profile: </span>
                {rj.investor_fit_analysis.lead_profile.required_profile}
              </p>
              <p className="whitespace-pre-line">
                <span className="font-medium">Why: </span>
                {rj.investor_fit_analysis.lead_profile.why}
              </p>
              {rj.investor_fit_analysis.lead_profile.proof_still_missing.length >
                0 && (
                <div>
                  <div className="mb-1 font-medium">Proof still missing:</div>
                  <ul className="list-disc pl-5 text-brand-ink">
                    {rj.investor_fit_analysis.lead_profile.proof_still_missing.map(
                      (p, i) => (
                        <li key={i}>{p}</li>
                      ),
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </Section>

      <Section title="13. Template Outreach Copy">
        <div className="space-y-5 text-[15px]">
          {isV2(oc) ? (
            <>
              <div>
                <h3 className="mb-2 text-[12px] font-medium uppercase tracking-[0.18em] text-brand-muted">
                  Cold email versions
                </h3>
                <div className="space-y-3">
                  <EmailBlockView
                    label="Institutional Lead"
                    email={oc.cold_emails.institutional_lead}
                  />
                  <EmailBlockView
                    label="Strategic Lead"
                    email={oc.cold_emails.strategic_lead}
                  />
                  <EmailBlockView
                    label="Momentum-Driven"
                    email={oc.cold_emails.momentum_driven}
                  />
                </div>
              </div>
              <CopyBlock label="LinkedIn message">
                {oc.linkedin_message}
              </CopyBlock>
              <div>
                <h3 className="mb-2 text-[12px] font-medium uppercase tracking-[0.18em] text-brand-muted">
                  Follow-up sequence
                </h3>
                <div className="space-y-3">
                  <EmailBlockView
                    label="7-day follow-up"
                    email={oc.follow_up_sequence.seven_day}
                  />
                  <EmailBlockView
                    label="14-day follow-up"
                    email={oc.follow_up_sequence.fourteen_day}
                  />
                  <EmailBlockView
                    label="CEO direct"
                    email={oc.follow_up_sequence.ceo_direct}
                  />
                </div>
              </div>
              <CopyBlock label="Warm intro request">
                {oc.warm_intro_request}
              </CopyBlock>
            </>
          ) : (
            <>
              <EmailBlockView
                label="Cold investor email"
                email={oc.cold_investor_email}
              />
              <CopyBlock label="LinkedIn message">
                {oc.linkedin_message}
              </CopyBlock>
              <EmailBlockView
                label="Follow-up email"
                email={oc.followup_email}
              />
              <CopyBlock label="Warm intro request">
                {oc.warm_intro_request}
              </CopyBlock>
            </>
          )}
        </div>
      </Section>

      <Section title="14. Data Room Checklist">
        <ul className="space-y-1 text-[15px]">
          {rj.data_room_checklist.map((d, i) => (
            <li key={i} className="flex items-start gap-2">
              <DataRoomPill s={d.status} />
              <span className="text-brand-ink">{d.item}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="15. Final Recommendation">
        <p className="text-[14px] font-medium text-brand-ink">
          {humanRecommendation(rj.final_recommendation.recommendation)}
        </p>
        <p className="mt-2 whitespace-pre-line text-[14px] leading-relaxed text-brand-ink">
          {rj.final_recommendation.rationale}
        </p>
        {rj.final_recommendation.projected_score_after_remediation && (
          <div className="mt-4 rounded-lg border border-brand-accent/30 bg-brand-accent/10 p-3.5 print:border-brand-accent/40">
            <div className="mb-1 text-[12px] font-semibold uppercase tracking-wider text-brand-accent">
              Projected readiness after priority remediation
            </div>
            <p className="text-[14px] leading-relaxed text-brand-ink">
              With the priority changes in Section 8 completed, expected
              readiness moves from{" "}
              <span className="font-semibold">
                {rj.investor_readiness_score.overall}
              </span>{" "}
              to{" "}
              <span className="font-semibold">
                {
                  rj.final_recommendation.projected_score_after_remediation
                    .score_range_low
                }
                {" to "}
                {
                  rj.final_recommendation.projected_score_after_remediation
                    .score_range_high
                }
              </span>
              . At that level the round becomes credible for{" "}
              {
                rj.final_recommendation.projected_score_after_remediation
                  .unlocks_audience
              }
              .
            </p>
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-brand-line bg-brand-paper p-5 print:break-inside-avoid">
      <h2 className="mb-3 font-serif text-[18px] text-brand-ink">{title}</h2>
      {children}
    </section>
  );
}

function CopyBlock({
  label,
  children,
}: {
  label: string;
  children: string | React.ReactNode;
}) {
  const text = typeof children === "string" ? children : null;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-[12px] font-medium uppercase tracking-[0.15em] text-brand-muted">
          {label}
        </span>
        {text != null && <CopyButton text={text} />}
      </div>
      <div className="whitespace-pre-line rounded-md border border-brand-line bg-brand-paper-2/40 p-3 text-[15px] text-brand-ink">
        {children}
      </div>
    </div>
  );
}

function EmailBlockView({
  label,
  email,
}: {
  label: string;
  email: EmailBlock;
}) {
  const combined = `Subject: ${email.subject}\n\n${email.body}`;
  return (
    <div className="rounded-lg border border-brand-line bg-brand-paper/60 p-3 print:break-inside-avoid">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[12px] font-medium uppercase tracking-[0.18em] text-brand-muted">
          {label}
        </span>
        <CopyButton text={combined} />
      </div>
      <div className="text-[12px] font-medium uppercase tracking-[0.15em] text-brand-muted">
        Subject
      </div>
      <div className="mb-2 text-brand-ink">{email.subject}</div>
      <div className="text-[12px] font-medium uppercase tracking-[0.15em] text-brand-muted">
        Body
      </div>
      <div className="whitespace-pre-line text-brand-ink">{email.body}</div>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      className="rounded border border-brand-line bg-brand-paper px-2 py-0.5 text-[14px] font-medium uppercase tracking-wide text-brand-muted hover:text-brand-ink print:hidden"
    >
      Copy
    </button>
  );
}

function SeverityPill({ s }: { s: Severity }) {
  const cls =
    s === "high"
      ? "bg-destructive/15 text-destructive"
      : s === "medium"
      ? "bg-brand-accent/15 text-brand-accent"
      : "bg-brand-line text-brand-muted";
  return (
    <span className={`rounded px-1.5 py-0.5 text-[14px] font-medium uppercase tracking-wide ${cls}`}>
      {s}
    </span>
  );
}

function ExecSummarySynthesisView({
  synth,
  strengths,
}: {
  synth: NonNullable<ReportJson["exec_summary_synthesis"]>;
  strengths?: ReportJson["strengths"];
}) {
  const topStrengths = (strengths ?? []).slice(0, 3);
  return (
    <div className="space-y-5">
      <p className="whitespace-pre-line text-[14px] leading-relaxed text-brand-ink">
        {synth.narrative}
      </p>

      {synth.top_priorities.length > 0 && (
        <div>
          <div className="mb-2 text-[12px] font-medium uppercase tracking-[0.15em] text-brand-muted">
            Top Priorities
          </div>
          <ul className="space-y-2 text-[15px]">
            {synth.top_priorities.map((p, i) => (
              <li
                key={i}
                className={`flex items-start gap-2 ${
                  i > 0 ? "border-t border-brand-line pt-2" : ""
                }`}
              >
                <SeverityPill s={p.level} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-brand-ink">{p.title}</div>
                  <div className="mt-0.5 text-[14px] text-brand-muted">
                    {p.why_it_matters}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {topStrengths.length > 0 && (
        <div>
          <div className="mb-2 text-[12px] font-medium uppercase tracking-[0.15em] text-brand-muted">
            Key Strengths
          </div>
          <ul className="space-y-1.5 text-[15px]">
            {topStrengths.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-brand-ink">
                <span aria-hidden className="mt-[2px] text-emerald-700">✓</span>
                <span>{s.strength}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <div className="mb-2 text-[12px] font-medium uppercase tracking-[0.15em] text-brand-muted">
          Likely Investor Reaction
        </div>
        <p className="whitespace-pre-line text-[15px] leading-relaxed text-brand-muted">
          {synth.investor_reaction_summary}
        </p>
      </div>

      <div className="rounded-lg border border-brand-line bg-brand-paper-2/40 p-3">
        <div className="mb-1 text-[12px] font-medium uppercase tracking-[0.15em] text-brand-muted">
          Next Step
        </div>
        <p className="whitespace-pre-line text-[15px] leading-relaxed text-brand-ink">
          {synth.next_step}
        </p>
      </div>
    </div>
  );
}

function DataRoomPill({ s }: { s: DataRoomStatus }) {
  // Back-compat: map legacy `have`/`missing` onto the new enum.
  const normalized: "confirmed" | "partial" | "to_confirm" =
    s === "have"
      ? "confirmed"
      : s === "missing"
      ? "to_confirm"
      : (s as "confirmed" | "partial" | "to_confirm");
  const cls =
    normalized === "confirmed"
      ? "bg-emerald-100 text-emerald-800"
      : normalized === "partial"
      ? "bg-amber-100 text-amber-800"
      : "bg-destructive/15 text-destructive";
  const label =
    normalized === "confirmed"
      ? "Confirmed"
      : normalized === "partial"
      ? "Partial"
      : "To Confirm";
  return (
    <span className={`rounded px-1.5 py-0.5 text-[14px] font-medium uppercase tracking-wide ${cls}`}>
      {label}
    </span>
  );
}

function ReadinessPill({ r }: { r: Readiness }) {
  const cls =
    r === "ready"
      ? "bg-emerald-100 text-emerald-800"
      : "bg-amber-100 text-amber-800";
  const label = r === "ready" ? "Ready" : "Needs Work";
  return (
    <span
      className={`shrink-0 rounded px-1.5 py-0.5 text-[14px] font-medium uppercase tracking-wide ${cls}`}
    >
      {label}
    </span>
  );
}

function humanize(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function humanRecommendation(r: string): string {
  if (r === "strong") return "Strong";
  if (r === "proceed_with_changes") return "Proceed with changes";
  if (r === "not_ready") return "Not ready";
  return r;
}